'use client'

import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'

interface DialogContextType {
  openDialogIds: Set<DIALOG>
  openDialog: (dialogId: DIALOG) => void
  closeDialog: (dialogId: DIALOG) => void
  isDialogOpen: (dialogId: DIALOG) => boolean
  toggleDialog: (dialogId: DIALOG) => void
}

const DialogContext = createContext<DialogContextType | undefined>(undefined)

export const DialogProvider = ({ children }: { children: ReactNode }) => {
  const [openDialogIds, setOpenDialogIds] = useState<Set<DIALOG>>(new Set())

  const openDialog = useCallback((dialogId: DIALOG) => {
    setOpenDialogIds((prev) => new Set(prev).add(dialogId))
  }, [])

  const closeDialog = useCallback((dialogId: DIALOG) => {
    setOpenDialogIds((prev) => {
      const newSet = new Set(prev)
      newSet.delete(dialogId)

      return newSet
    })
  }, [])

  const isDialogOpen = useCallback(
    (dialogId: DIALOG) => {
      return openDialogIds.has(dialogId)
    },
    [openDialogIds],
  )

  const toggleDialog = useCallback(
    (dialogId: DIALOG) => {
      if (isDialogOpen(dialogId)) {
        closeDialog(dialogId)
      } else {
        openDialog(dialogId)
      }
    },
    [isDialogOpen, closeDialog, openDialog],
  )

  // Memoize the context value to ensure stable references
  const value = useMemo(
    () => ({
      openDialogIds,
      openDialog,
      closeDialog,
      isDialogOpen,
      toggleDialog,
    }),
    [openDialogIds, openDialog, closeDialog, isDialogOpen, toggleDialog],
  )

  return <DialogContext.Provider value={value}>{children}</DialogContext.Provider>
}

export const useDialog = () => {
  const context = useContext(DialogContext)

  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider')
  }

  return context
}

export enum DIALOG {
  LOCALE = 'locale',
  NEWSLETTER = 'newsletter',
  OUT_OF_STOCK_SIGNUP = 'outOfStockSignup',
}
